import React, { useContext, useEffect, useRef, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import moment from 'moment';
import { Badge, Box, Flex, Image, Spinner } from "theme-ui";

import { getUsers, checkUser, updateUser, inviteUser, deleteUser, resendInvite, restoreUser } from "api/Users";
import { getRoles } from "api/Roles";
import { trackActivity } from "api/ActivityTracker";

import AnnouncementHistory from "components/Announcement/AnnouncementHistory";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "components/ui/Modal";
import Span from "components/typography/Span";
import Header from "components/ui/Header";
import TableData from "components/ui/TableData";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import SelectInput from "components/forms/SelectInput";
import { useToast } from "components/toast";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import PlusIcon from "components/svg/icons/PlusIcon";
import { themevals } from "theme/themevals";
import PlaneIcon from "components/svg/icons/PlaneIcon";
import { InviteWrapper, UserInfoWrapper, EllipsisWrapper, ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "./styled";
import AvatarIcon from "components/svg/icons/AvatarIcon";
import CrownIcon from "components/svg/icons/CrownIcon";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import EllipsisIcon from "components/svg/icons/EllipsisIcon";
import {UsersIcon} from "components/svg";
import TrashIcon from "components/svg/icons/TrashIcon";
import {CheckMarkIcon}from "components/svg";
import {QuestionMarkIcon} from "components/svg";
import UserContext from "context/User";

interface Role {
  id: string; 
  title: string;
}

export default function Users() {

  const toast = useToast();  
  const { user: currentUser } = useContext<any>(UserContext);
  const lastTrackedQuery = useRef<string | null>(null);

  const [refreshData, setRefreshData] = useState(false);  

  const [roleList, setRoleList] = useState<any[]>([]);
  const [editModal, toggleEditModal] = useState<boolean>(false);
  const [inviteModal, toggleInviteModal] = useState<boolean>(false);

  const [userInfo, setUserInfo] = useState<any>(null);  

  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);

  const columnHelper = createColumnHelper();

  const formatDate = (dateString: string): string => {
    return moment(dateString).format('MMM D, YYYY');
  };

  //Senty Testing
  // useEffect(() => {
    // if (process.env.REACT_APP_ENVIRONMENT !== 'production') {
    //   throw new Error("Test Sentry error from useEffect!");
    // }
  //   _testSentry();

  // }, []);

  // const _testSentry = async () => { 
  //   const res = await testSentry();
  // }

  const columns = [
    columnHelper.accessor((row: any) => row.data, {
      id: "user_info",
      cell: (info: any) => (
        <UserInfoWrapper onClick={() => setEditModal(info.row.original)}>
          {info.getValue().logo ?
            <Image width="40px" height="40px" src={info.getValue().logo} sx={{borderRadius:"6px"}}/>
          :
            <AvatarIcon fill={"#BFBFBF"} width={"40px"}/>
          }          
          <div>
            <Span color="#000" fontWeight="500">{info.row.original.firstName + " " + info.row.original.lastName}</Span>
            <Span color="var(--theme-ui-colors-base_500)" display="block">{info.row.original.email}</Span>
          </div>
        </UserInfoWrapper>
      ),
      header: () => <span>User Info</span>,
      enableSorting: false,
    }),
    columnHelper.accessor((row: any) => row?.role?.title, {
      id: "role",
      cell: (info: any) => (        
        ["Admin","Administrator"].includes(info.getValue()) ? <Badge as="span" sx={{gap: "10px"}} variant="caution"><span>{info.getValue()}</span> <CrownIcon fill={"#CAA11C"} width="14px"/></Badge> : info.getValue()
      ),     
      header: () => <span>Role</span>,
      meta: {
        style: { width: '200px' },
      },
    }),
    columnHelper.accessor("status", {
      id: "status",
      cell: (info) => {
        const [inviteText, setInviteText] = React.useState("Invite Sent");
        const [isProcessing, setIsProcessing] = React.useState(false);
    
        const handleMouseEnter = () => {
          if (!isProcessing) setInviteText("Resend Invite");
        };
    
        const handleMouseLeave = () => {
          if (!isProcessing) setInviteText("Invite Sent");
        };
    
        const handleClick = async () => {
          setIsProcessing(true);
          try {
            await resendUserInvite(info.row.original);
          } catch (error) {
            console.error("Failed to resend invite:", error);
          } finally {
            setIsProcessing(false);
          }
        };
    
        switch (info.getValue()) {
          case 1:
            return <Badge as="span" variant="success">Active</Badge>;
          case 2:
            return (
              <InviteWrapper
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
              >
                <Badge as="span" sx={{gap: "10px"}} variant="info">
                  <span>{inviteText}</span>
                  {!isProcessing ? (
                    <PlaneIcon fill="#104E9A" width="14px" />
                  ) : (
                    <Spinner sx={{color: "#104E9A"}} size={14} />
                  )}                    
                </Badge>
              </InviteWrapper>
            );
          default:
            return <Badge as="span" variant="danger">Inactive</Badge>;
        }
      },
      header: () => <span>Status</span>,
      meta: {
        style: { width: "200px" },
      },
    }),
    columnHelper.accessor("lastActiveAt", {
      id: "lastActiveAt",
      cell: (info: any) => (        
        <div>
            {info.getValue() !== null ? formatDate(info.getValue()) : null}
        </div>
      ),     
      header: () => <span>Last Active</span>,
      meta: {
        style: { width: '200px' },
      },
    }),
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      cell: (info: any) => (        
        <div>
            {formatDate(info.getValue())}
        </div>
      ),     
      header: () => <span>Date Added</span>,
      meta: {
        style: { width: '200px' },
      },
    }),
    columnHelper.accessor("actions", {
      id: "actions",
      cell: (info: any) => {
        const items = [
          { label: "Edit", onClick: () => setEditModal(info.row.original) },
          ...(info.row.original.status === 1
            ? [
                {
                  label: "Deactivate",
                  style: { color: themevals.colors.danger },
                  onClick: () => submitDeleteUser(info.row.original),
                },
              ]
            : []),
          ...(info.row.original.status === 0
            ? [
                {
                  label: "Reactivate",
                  style: { color: themevals.colors.success },
                  onClick: () => submitReactivateUser(info.row.original),
                },
              ]
            : []),
        ];
    
        return (
          <EllipsisWrapper>
            <DropDownMenu
              toggleComponent={
                <EllipsisIcon fill={"#818181"} width="20px" height="20px" />
              }
              items={items}
            />
          </EllipsisWrapper>
        );
      },
      header: () => <span></span>,
      meta: {
        style: { width: "10px" },
      },
    }),
  ];
  
  

  const setEditModal = (the_user: any) => {      
    if(the_user){
      setUserInfo(the_user);
      toggleEditModal(true);
    } else {
      setUserInfo(null);
      toggleEditModal(false);
    }
  };  

  const UserSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Email is required."),
    firstName: Yup.string().required("First Name is required."),
    lastName: Yup.string().required("Last Name is required."),   
    role: Yup.object().shape({
      id: Yup.string().required('Role ID is required'),
    }).required('Role is required')
  }).noUnknown(true);

  const submitUserUpdate = async (values: any, actions: any) => {  
    const original = userInfo || {};
    const changedFields: string[] = [];

    if ((original?.firstName ?? "") !== (values.firstName ?? "")) {
      changedFields.push("firstName");
    }
    if ((original?.lastName ?? "") !== (values.lastName ?? "")) {
      changedFields.push("lastName");
    }
    if ((original?.email ?? "") !== (values.email ?? "")) {
      changedFields.push("email");
    }

    const originalRoleId =
      original?.role?.id ?? original?.roleId ?? (typeof original?.role === "string" ? original.role : null);
    const updatedRoleId =
      values?.role?.id ?? values?.roleId ?? (typeof values?.role === "string" ? values.role : null);

    if ((originalRoleId ?? null) !== (updatedRoleId ?? null)) {
      changedFields.push("role");
    }

    const update = {
      firstName : values.firstName,
      lastName: values.lastName,
      email: values.email,
      role: values.role,
      jobTitle: values.jobTitle ? String(values.jobTitle).trim() : null,
      phone: values.phone ? String(values.phone).trim() : null,
    }
    
    const res = await updateUser({ data : userInfo, update });
    if(res?.firstName){ 
      toast.add("User updated.","var(--theme-ui-colors-success)");
      setRefreshData(true);
      setTimeout(() => { setRefreshData(false) }, 100);   

      if (changedFields.length > 0) {
        void trackActivity({
          module: "users",
          action: "update_user",
          actionType: "update",
          userId: currentUser?.id || null,
          companyId: currentUser?.company?.id || null,
          entityType: "user",
          entityId: res?.id ?? original?.id ?? null,
          metadata: {
            changedFields,
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });

        if (changedFields.includes("role")) {
          void trackActivity({
            module: "users",
            action: "change_user_role",
            actionType: "update",
            userId: currentUser?.id || null,
            companyId: currentUser?.company?.id || null,
            entityType: "user",
            entityId: res?.id ?? original?.id ?? null,
            metadata: {
              oldRoleId: originalRoleId ?? null,
              newRoleId: updatedRoleId ?? null,
            },
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
          });
        }
      }
    }else{
      toast.add(res?.message ? res.message : "Error updating user","var(--theme-ui-colors-danger)");
    }    
    toggleEditModal(false);        
  }; 

  const submitInviteUser = async (values: any, { setErrors}: any) => {
  
    const invite = {
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      role: values.role,
    };
      
    const check = await checkUser({ invite });
    if(check.length > 0) {
      setErrors({ email: `${invite.email} already exists and can't be invited again` });
    } else {
      const res = await inviteUser({ invite });
      if(res?.success){
        toast.add(res?.message ? res.message : "User invited","var(--theme-ui-colors-success)");
        void trackActivity({
          module: "users",
          action: "create_user",
          actionType: "create",
          userId: currentUser?.id || null,
          companyId: currentUser?.company?.id || null,
          entityType: "user",
          entityId: res?.userId ?? null,
          metadata: {
            userId: res?.userId ?? null,
            email: invite.email || null,
            roleId: invite.role?.id ?? invite.role ?? null,
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
        toggleInviteModal(false);    
        setRefreshData(true);
        setTimeout(() => { setRefreshData(false) }, 100);   
      } else {  
        toast.add(res?.message ? res.message : "Error inviting user","var(--theme-ui-colors-danger)");
      }
    } 
  };  

  const InviteSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Email is required."),
    firstName: Yup.string().required("First Name is required."),
    lastName: Yup.string().required("Last Name is required."),
    role: Yup.object().shape({
      id: Yup.string().required('Role ID is required'),
    }).required('Role is required'),  });  

  const confirmDeleteUser = async (targetUser:any) => { 
    setConfirmSubmit(true);   
    const res = await deleteUser({ data: targetUser });
    if(res?.success){ 
      toast.add("User deactivated.","var(--theme-ui-colors-success)");
      void trackActivity({
        module: "users",
        action: "deactivate_user",
        actionType: "update",
        userId: currentUser?.id || null,
        companyId: currentUser?.company?.id || null,
        entityType: "user",
        entityId: targetUser?.id ?? null,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      setRefreshData(true);    
      setTimeout(() => { setRefreshData(false) }, 100);   
    }else{
      toast.add(res?.message ? res.message : "Error deactivating user.","var(--theme-ui-colors-danger)");
    } 
    setConfirmSubmit(false);   
    setConfirmModal(false)          
  };  
  
  const confirmReactivateUser = async (targetUser: any) => {
    setConfirmSubmit(true);
    const res = await restoreUser({ id: targetUser.id });
    if (res?.success) {
      toast.add("User reactivated.","var(--theme-ui-colors-success)");
      void trackActivity({
        module: "users",
        action: "reactivate_user",
        actionType: "update",
        userId: currentUser?.id || null,
        companyId: currentUser?.company?.id || null,
        entityType: "user",
        entityId: targetUser?.id ?? null,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      setRefreshData(true);
      setTimeout(() => { setRefreshData(false) }, 100);
    } else {
      toast.add(res?.message ? res.message : "Error reactivating user.","var(--theme-ui-colors-danger)");
    }
    setConfirmSubmit(false);
    setConfirmModal(false);
  };
  
  const closeConfirmModal = async () => {
    setConfirmModal(false)
  }

  const submitDeleteUser = async (user?:any) => {
    const targetUser = user || userInfo;
    toggleEditModal(false);  
    setConfirmModal(
    <BasicModal 
      title="Deactivate user?" 
      cancel_click={closeConfirmModal} 
      cancel_text="No, cancel" 
      $submitting={confirmSubmit}
      size={"sm"}
      icon={<QuestionMarkIcon fill={"#818181"} height="30px"/>}
      actions={<BasicButton
        type="button"    
        scheme="danger"      
        onClick={() => confirmDeleteUser(targetUser)} 
        $submitting={confirmSubmit}
        $iconHeight="14px"
        $iconWidth="14px"
        $loadingColor="#fff"
        keepText={true}
        styles={{margin:"0"}}
      >
        {!confirmSubmit &&
        <TrashIcon fill={"#fff"}/>
       }
        
        {"Deactivate"}</BasicButton>}
    
      >
      <P>Are you sure you want to deactivate <strong style={{color:themevals.colors.black}}>{targetUser?.firstName} {targetUser?.lastName}</strong>?</P>
    </BasicModal>);
  }

  const submitReactivateUser = async (user: any) => {
    const targetUser = user;
    toggleEditModal(false);
    setConfirmModal(
      <BasicModal
        title="Reactivate user?"
        cancel_click={closeConfirmModal}
        cancel_text="No, cancel"
        $submitting={confirmSubmit}
        size={"sm"}
        icon={<QuestionMarkIcon fill={"#818181"} height="30px"/>}
        actions={
          <BasicButton
            type="button"
            onClick={() => confirmReactivateUser(targetUser)}
            $submitting={confirmSubmit}
            $iconHeight="14px"
            $iconWidth="14px"
            $loadingColor="#fff"
            keepText={true}
            styles={{
              margin: "0",
              backgroundColor: themevals.colors.success,
              borderColor: themevals.colors.success,
              color: "#fff",
            }}
          >
            {!confirmSubmit && <CheckMarkIcon fill={"#fff"} />}
            Reactivate
          </BasicButton>
        }
      >
        <P>Are you sure you want to reactivate <strong style={{color:themevals.colors.black}}>{targetUser?.firstName} {targetUser?.lastName}</strong>?</P>
      </BasicModal>
    );
  };
  

  const resendUserInvite = async (user: any) => {
    const res = await resendInvite({ data: user });
    if(res?.success){ 
      toast.add("Invite resent.","var(--theme-ui-colors-success)");
      setRefreshData(true);    
      setTimeout(() => { setRefreshData(false) }, 100);          
    }else{
      toast.add(res?.message ? res.message : "Error resending invite.","var(--theme-ui-colors-danger)");
    }          
  }

  const toggleEdit = () => toggleEditModal(!editModal);
  const toggleInvite = () => toggleInviteModal(!inviteModal);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await getRoles({ query: { recordsPer: 1000 } });
        if (res.items) {
          const roles = (res.items as Role[]).map((item) => ({
            value: item.id,
            label: item.title,
          }));
          setRoleList(roles);
        }
      } catch {
        toast.add("Failed to fetch roles.", "var(--theme-ui-colors-red)");
      }
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Users"
  }, []);

  return (
    <>
      <Header></Header>      
      <Page title="Users" subtitle="Manage user access.">                
        <TableData 
          title={"All Users"}
          defaultFilters={{"client.id" : ":null", "provider": ":null"}}
          api={getUsers} 
          columns={columns} 
          refresh={refreshData} 
          returnQuery={(query: any) => {
            if (!query) return;

            const rawFilters = query?.filters ?? {};
            const cleanedFilters = Object.entries(rawFilters).reduce<Record<string, string>>((acc, [key, value]) => {
              if (value === undefined || value === null || value === "" || value === ":null") {
                return acc;
              }
              acc[key] = value as string;
              return acc;
            }, {});

            const signature = JSON.stringify({
              page: query?.pageNum ?? null,
              limit: query?.recordsPer ?? null,
              filters: cleanedFilters,
            });
            if (lastTrackedQuery.current === signature) {
              return;
            }
            lastTrackedQuery.current = signature;
        void trackActivity({
          module: "users",
          action: "view_users_list",
          actionType: "view",
          userId: currentUser?.id || null,
          companyId: currentUser?.company?.id || null,
          entityType: "user_list",
              entityId: null,
              metadata: {
                page: query?.pageNum ?? null,
                limit: query?.recordsPer ?? null,
                filters: Object.keys(cleanedFilters).length > 0 ? cleanedFilters : {},
              },
              userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
            });
          }}
          actions={[
            <BasicButton 
              key="1"
              scheme="primary"              
              onClick={toggleInvite}
              $iconHeight="10px" 
            ><PlusIcon fill="#fff"/>Add user</BasicButton>,            
          ]} 
        />
      </Page>
      <Modal isOpen={editModal} toggle={toggleEdit} centered={true} size="sm">
        <Formik initialValues={{...userInfo }} onSubmit={submitUserUpdate} validationSchema={UserSchema} >
          {({ isSubmitting, errors, values, submitCount, setFieldValue }: any) => { 
            useEffect(() => {
              if (roleList.length > 0 && !values.roles) {
                setFieldValue("roles", roleList[0].value);
              }
            }, [roleList, values.roles, setFieldValue]);
            return (                
            <Form noValidate autoComplete="off">
              <ModalHeader toggle={toggleEdit}><ModalIcon><UsersIcon fill={"000"}/></ModalIcon><ModalTitleWrapper><ModalTitle>Edit user</ModalTitle><ModalSubtitle>{"Edit a user's details."}</ModalSubtitle></ModalTitleWrapper></ModalHeader>
              <ModalBody> 
                <TextInput 
                  name="firstName"
                  label="First Name"
                  value={values.firstName}
                  onChange={(val: any) => setFieldValue("firstName",val)}
                  $errors={errors.firstName && submitCount > 0 ? errors.firstName : null}                                    
                /> 
                <TextInput 
                  name="lastName"
                  label="Last Name"
                  value={values.lastName}
                  onChange={(val: any) => setFieldValue("lastName",val)}
                  $errors={errors.lastName && submitCount > 0 ? errors.lastName : null}                                    
                /> 
                <TextInput
                  name="jobTitle"
                  label="Job title (on proposals they send)"
                  value={values.jobTitle ?? ""}
                  onChange={(val: any) => setFieldValue("jobTitle", val)}
                />
                <TextInput
                  name="phone"
                  label="Phone (on proposals they send)"
                  value={values.phone ?? ""}
                  onChange={(val: any) => setFieldValue("phone", val)}
                />
                <TextInput 
                  type="email" 
                  name="email" 
                  label="Email"
                  value={values.email}
                  onChange={(val: any) => setFieldValue("email",val)}
                  $errors={errors.email && submitCount > 0 ? errors.email : null}                                    
                />                
                {roleList.length > 0 && <SelectInput 
                  name="role" 
                  label="Role"                       
                  value={values?.role?.id}
                  onChange={(val: any) => setFieldValue("role.id",val)}
                  options={roleList}  
                  $errors={errors.role && submitCount > 0 ? errors.role : null}  
                  $customStyles={{marginBottom:"30px"}}
                />}                                                                     
                <AnnouncementHistory userId={userInfo?.id} />
              </ModalBody>
              <ModalFooter>
                <Flex sx={{display: "flex", flexWrap : "wrap-reverse", gap : "20px", justifyContent : "space-between", width : "100%"}}>
                  <Box>
                    <BasicButton 
                      scheme="dangerSecondary"
                      styles={{margin:".25rem 0.625rem .25rem 0"}}
                      type="button" 
                      onClick={() => submitDeleteUser()} 
                      $iconHeight="14px"
                      $iconWidth="14px"
                      $loadingColor="#fff"
                    ><TrashIcon fill={"#A60000"}/>Deactivate</BasicButton> 
                  </Box>
                  <Flex sx={{display: "flex", flexWrap : "wrap-reverse"}}>
                    <BasicButton 
                      outline={true}               
                      styles={{margin:".25rem 20px .25rem 0"}}
                      type="button" 
                      onClick={toggleEdit} 
                      scheme="secondary"
                    >Cancel</BasicButton>   
                    <BasicButton 
                      styles={{margin:".25rem 0 .25rem 0"}}
                      type="submit" 
                      $submitting={isSubmitting}
                      $iconHeight="14px"
                      $loadingColor="#fff"
                      keepText={true}
                    >
                      {!isSubmitting &&
                        <CheckMarkIcon fill={"#fff"}/>
                        }
                      Update User</BasicButton>
                  </Flex>
                </Flex>                
              </ModalFooter>
            </Form>
            );
          }}
        </Formik>
      </Modal>
      <Modal isOpen={inviteModal} toggle={toggleInvite} centered={true} size="sm">
        <Formik initialValues={{email:"",role:null,firstName:"",lastName:""}} onSubmit={submitInviteUser} validationSchema={InviteSchema} >
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => {
            useEffect(() => {
              if (roleList.length > 0 && !values?.role?.id) {
                setFieldValue("role.id", roleList[0].value);
              }
            }, [roleList, values.role, setFieldValue]);
            return (              
            <Form noValidate autoComplete="off">
              <ModalHeader toggle={toggleInvite}><ModalIcon><UsersIcon fill={"000"}/></ModalIcon><ModalTitleWrapper><ModalTitle>Add user</ModalTitle><ModalSubtitle>Add a new user to your company.</ModalSubtitle></ModalTitleWrapper></ModalHeader>
              <ModalBody> 
                <TextInput 
                  name="firstName" 
                  label="First Name" 
                  value={values.firstName}
                  onChange={(val: any) => setFieldValue("firstName",val)}
                  autoComplete="off"
                  $errors={errors.firstName && submitCount > 0 ? errors.firstName : null}  
                />
                <TextInput 
                  name="lastName" 
                  label="Last Name" 
                  value={values.lastName}
                  onChange={(val: any) => setFieldValue("lastName",val)}
                  autoComplete="off"
                  $errors={errors.lastName && submitCount > 0 ? errors.lastName : null} 
                />                                              
                <TextInput
                  type="email" 
                  name="email" 
                  label="Email"  
                  value={values.email}
                  onChange={(val: any) => setFieldValue("email",val)}
                  autoComplete="off"
                  $errors={errors.email && submitCount > 0 ? errors.email : null}  
                />
                {roleList.length > 0 && <SelectInput 
                  name="role" 
                  label="Role" 
                  value={values?.role?.id}
                  onChange={(selectedOption: any) =>
                    setFieldValue("role.id", selectedOption)
                  }
                  options={roleList}  
                  $errors={errors.role && submitCount > 0 ? errors.role : null} 
                  $customStyles={{marginBottom:"30px"}} 
                />}                
              </ModalBody>
              <ModalFooter>
                <BasicButton 
                  outline={true}               
                  styles={{margin:".25rem 10px .25rem 0"}}
                  type="button" 
                  onClick={toggleInvite} 
                  scheme="secondary"
                >Cancel</BasicButton>
                <BasicButton 
                  styles={{margin:".25rem 0 .25rem 0"}}
                  type="submit" 
                  $submitting={isSubmitting}
                  $iconHeight="14px"
                  $loadingColor="#fff"
                  keepText={true}
                >  {!isSubmitting &&
                  <CheckMarkIcon fill={"#fff"}/>
                  }Send invite</BasicButton>                
              </ModalFooter>
            </Form>
          );
        }}            
        </Formik>
      </Modal>
      {confirmModal}      
    </>
  );
}
